import React from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BoltIcon from '@mui/icons-material/Bolt';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import InlineAlert from '../../../shared/components/InlineAlert';
import StatusChip from '../../../shared/components/StatusChip';
import { relativeTime } from './pipeline/utils';
import { futureTime, overallAccent } from './healthUtils';
import { domainLabel } from '../../../shared/labels';
import type { ReconcilerHealth, ScheduleHealth } from '../../../types/backend';

/** Trigger-aware "Next run" cell value. */
function nextRunLabel(schedule: ScheduleHealth): string {
  switch (schedule.trigger_kind) {
    case 'scheduled':
      if (schedule.enabled === false) return 'Paused';
      return schedule.next_tick ? futureTime(schedule.next_tick) : 'unknown';
    case 'event_driven':
      return 'Event-driven';
    case 'manual':
      return 'On demand';
    default:
      return 'unknown';
  }
}

interface ReconcilerTileProps {
  health: ReconcilerHealth;
  selected: boolean;
  onSelect: (domain: string) => void;
}

const ReconcilerTile: React.FC<ReconcilerTileProps> = ({ health, selected, onSelect }) => {
  const isDown = health.overall === 'DOWN';
  const isUnknown = health.overall === 'UNKNOWN';
  const schedule = health.schedule;
  const isScheduled = schedule.trigger_kind === 'scheduled';
  const failedInvocations = health.invocation.failed_invocations ?? 0;
  // Invocation health is a schedule-firing signal — only meaningful for scheduled.
  const invocationDark = isScheduled && (!health.invocation.firing || failedInvocations > 0);
  const activeAlarms = (health.alarms ?? []).filter((a) => a.state === 'ALARM');

  return (
    <Paper
      variant="outlined"
      onClick={() => onSelect(health.domain)}
      sx={{
        p: 2,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        borderColor: isDown
          ? 'error.main'
          : isUnknown
            ? 'text.secondary'
            : selected
              ? 'primary.main'
              : 'divider',
        borderWidth: isDown || selected ? 2 : 1,
        borderStyle: isUnknown ? 'dashed' : 'solid',
        bgcolor: isDown ? (t) => alpha(t.palette.error.main, 0.08) : 'background.paper',
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover': {
          borderColor: isDown ? 'error.dark' : 'primary.main',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              flexShrink: 0,
              bgcolor: (t) => overallAccent(health.overall, t),
            }}
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {domainLabel(health.domain)}
          </Typography>
        </Box>
        <StatusChip status={health.overall} size="small" />
      </Box>

      {health.reason && (
        <Typography variant="body2" color="text.secondary">
          {health.reason}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Last run
          </Typography>
          <Typography variant="body2">{relativeTime(health.recent.last_run) || '—'}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {isScheduled ? 'Next run' : 'Trigger'}
          </Typography>
          <Typography variant="body2">{nextRunLabel(schedule)}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {isScheduled ? (
          <Tooltip
            title={
              invocationDark
                ? `Schedule unhealthy${failedInvocations > 0 ? ` · ${failedInvocations} failed` : ''}${!health.invocation.firing ? ' · not firing' : ''}`
                : 'Schedule firing normally'
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {invocationDark ? (
                <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
              ) : (
                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
              )}
              <Typography variant="caption" color={invocationDark ? 'error.main' : 'text.secondary'}>
                Invocation
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Tooltip
            title={
              schedule.trigger_kind === 'event_driven'
                ? schedule.source ?? 'Wakes on matching events'
                : 'Runs on demand'
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {schedule.trigger_kind === 'event_driven' ? (
                <BoltIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              ) : (
                <TouchAppIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              )}
              <Typography variant="caption" color="text.secondary">
                {schedule.trigger_kind === 'event_driven' ? 'Event-driven' : 'Manual'}
              </Typography>
            </Box>
          </Tooltip>
        )}

        {activeAlarms.length > 0 && (
          <Tooltip title={activeAlarms.map((a) => a.name).join(', ')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <NotificationsActiveIcon sx={{ fontSize: 16, color: 'error.main' }} />
              <Typography variant="caption" color="error.main">
                {activeAlarms.length} alarm{activeAlarms.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
};

export interface ControlPlaneOverviewPanelProps {
  reconcilers: ReconcilerHealth[];
  loading: boolean;
  error: string | null;
  selectedDomain: string | null;
  onSelect: (domain: string) => void;
}

const ControlPlaneOverviewPanel: React.FC<ControlPlaneOverviewPanelProps> = ({
  reconcilers,
  loading,
  error,
  selectedDomain,
  onSelect,
}) => {
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Box sx={{ p: 3 }}><InlineAlert severity="error">{error}</InlineAlert></Box>;
  }

  const downCount = reconcilers.filter((r) => r.overall === 'DOWN').length;
  const degradedCount = reconcilers.filter((r) => r.overall === 'DEGRADED').length;
  const unknownCount = reconcilers.filter((r) => r.overall === 'UNKNOWN').length;
  const idleCount = reconcilers.filter((r) => r.overall === 'IDLE').length;
  const healthyCount = reconcilers.filter((r) => r.overall === 'HEALTHY').length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>
      {downCount > 0 && (
        <InlineAlert severity="error">
          {downCount} reconciler{downCount !== 1 ? 's' : ''} DOWN — control-plane convergence is impaired.
        </InlineAlert>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 3, flex: 1, textAlign: 'center' }}>
          <Typography variant="h3" color={downCount > 0 ? 'error.main' : 'text.disabled'}>{downCount}</Typography>
          <Typography variant="body2" color="text.secondary">Down</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 3, flex: 1, textAlign: 'center' }}>
          <Typography variant="h3" color={degradedCount > 0 ? 'warning.main' : 'text.disabled'}>{degradedCount}</Typography>
          <Typography variant="body2" color="text.secondary">Degraded</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 3, flex: 1, textAlign: 'center' }}>
          <Typography variant="h3" color={unknownCount > 0 ? 'text.secondary' : 'text.disabled'}>{unknownCount}</Typography>
          <Typography variant="body2" color="text.secondary">Unknown</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 3, flex: 1, textAlign: 'center' }}>
          <Typography variant="h3" color="text.disabled">{idleCount}</Typography>
          <Typography variant="body2" color="text.secondary">Idle</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 3, flex: 1, textAlign: 'center' }}>
          <Typography variant="h3" color="success.main">{healthyCount}</Typography>
          <Typography variant="body2" color="text.secondary">Healthy</Typography>
        </Paper>
      </Box>

      {reconcilers.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No reconcilers reporting health.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          {reconcilers.map((health) => (
            <ReconcilerTile
              key={health.domain}
              health={health}
              selected={selectedDomain === health.domain}
              onSelect={onSelect}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ControlPlaneOverviewPanel;
